import React from 'react';
import { Sun, Moon } from 'lucide-react';
import PrayerMarkers from './PrayerMarkers';

/**
 * Sun/Moon progress bar component showing current position in day/night cycle
 * With improved time formatting
 */
const SunProgressBar = ({ times, prayerTimes, showPrayerMarkers, timezone }) => {
  // Format times consistently in the location's timezone
  const formatTimeInLocationTimezone = (date) => {
    if (!date) return '';

    try {
      // If the date is already in the correct format, just use that
      if (typeof date === 'string') {
        return date;
      }

      // Use IANA timezone when available for correct location time
      if (timezone) {
        try {
          return date.toLocaleTimeString('en-US', {
            timeZone: timezone,
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        } catch {
          // Fall through to machine-local time
        }
      }

      // Fallback: use machine-local time
      const hours = date.getHours();
      const minutes = date.getMinutes();

      // Convert to 12-hour format
      const hours12 = hours % 12 || 12; // Convert 0 to 12
      const period = hours >= 12 ? 'PM' : 'AM';

      // Format with leading zeros for minutes
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return '';
    }
  };

  return (
    <div className="w-full my-6" role="progressbar" aria-valuenow={Math.round(times.progress * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={times.isDay ? 'Day progress' : 'Night progress'}>
      {/* Time indicators below the countdown timer */}
      <div className="w-full flex justify-between px-1 mb-2">
        <div className="flex flex-col items-start">
          <span className="text-xs tracking-wide text-white/60 uppercase font-light">Sunrise</span>
          <span className="font-medium text-white text-sm tracking-wide">{formatTimeInLocationTimezone(times.sunrise)}</span>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-xs tracking-wide text-white/60 uppercase font-light">Sunset</span>
          <span className="font-medium text-white text-sm tracking-wide">{formatTimeInLocationTimezone(times.sunset)}</span>
        </div>
      </div>
      
      <div className="relative h-28">
        {/* Progress track */}
        <div className="absolute top-0 w-full h-14 rounded-full overflow-hidden" 
          style={{
            boxShadow: "rgba(0, 0, 0, 0.8) 0px 4px 12px -4px inset, rgba(255, 255, 255, 0.15) 0px -4px 8px -2px inset, rgba(0, 0, 0, 0.4) 0px 2px 4px -2px inset",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            border: "1px solid rgba(0, 0, 0, 0.15)"
          }}>
            
          {/* Morning to Evening Gradient Background */}
          <div 
            className="h-full w-full absolute transition-opacity duration-1000"
            style={{
              background: "linear-gradient(to right, #FFE8C8, #FF9D6B, #774181)",
              opacity: times.isDay ? 0.6 : 0.3
            }}
          />
          
          {/* Night Gradient Background */}
          <div 
            className="h-full w-full absolute transition-opacity duration-1000"
            style={{
              background: "linear-gradient(to right, #1E2A52, #273469, #33459C)",
              opacity: times.isDay ? 0.1 : 0.5
            }}
          />
          
          {/* Progress overlay */}
          <div 
            className="h-full relative transition-all duration-1000"
            style={{ 
              width: `${times.progress * 100}%`,
              boxShadow: times.isDay 
                ? "inset -1px 0 3px rgba(255, 150, 50, 0.4)" 
                : "inset -1px 0 3px rgba(100, 150, 255, 0.4)"
            }}>
            
            {/* Subtle glowing edge at progress point */}
            <div className="absolute right-0 h-full w-2"
              style={{
                background: times.isDay ? 
                  "linear-gradient(to right, rgba(255, 200, 100, 0), rgba(255, 200, 100, 0.3))" : 
                  "linear-gradient(to right, rgba(120, 140, 255, 0), rgba(120, 140, 255, 0.3))",
                filter: "blur(1px)"
              }}
            />
          </div>
          
          {/* Prayer time markers - moved to separate component */}
          {showPrayerMarkers && prayerTimes && (
            <PrayerMarkers
              prayerTimes={prayerTimes}
              times={times}
              timezone={timezone}
            />
          )}
        </div>
        
        {/* Sun/Moon Indicator */}
        <div 
          className="absolute transition-all duration-500 z-30" 
          style={{ 
            left: `calc(${times.progress * 100}% - 12px)`,
            top: "9px", // Adjusted for the smaller size
          }}
        >
          {times.isDay ? (
            <div>
              <div 
                className="relative w-9 h-9 rounded-full bg-gradient-to-b from-yellow-300 to-yellow-500 flex items-center justify-center"
                style={{
                  boxShadow: "0 0 8px 2px rgba(255, 170, 0, 0.4), 0 3px 5px rgba(0, 0, 0, 0.2)",
                  border: "2px solid rgba(255, 255, 255, 0.7)"
                }}
              >
                <Sun size={16} className="text-yellow-100" />
                <div className="absolute top-1.5 left-1.5 w-1.5 h-1.5 rounded-full bg-white/70" />
              </div>
            </div>
          ) : (
            <div>
              <div 
                className="relative w-9 h-9 rounded-full bg-gradient-to-b from-blue-100 to-gray-300 flex items-center justify-center"
                style={{
                  boxShadow: "0 0 8px 2px rgba(100, 150, 255, 0.2), 0 3px 5px rgba(0, 0, 0, 0.2)",
                  border: "2px solid rgba(255, 255, 255, 0.7)"
                }}
              >
                <Moon size={14} className="text-gray-700" />
                <div className="absolute top-1.5 left-1.5 w-1.5 h-1.5 rounded-full bg-white/70" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SunProgressBar;